import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import http from 'core/services/http';
import Config from 'core/config';
import { getWhatsNewConfig } from 'features/dashboard/duck';
import { getEnabledNewFeatures } from 'features/dashboard/selectors';
import { useEventId } from 'features/chatbot/useEventId';
import { WhatsNewBanner } from './WhatsNewBanner';
import { WhatsNewModal } from './WhatsNewModal';
import './WhatsNewAnnouncement.scss';

const SEEN_KEY = 'whatsNewChatbotSeen';

function getSnapshot(features) {
  return JSON.stringify(features.map(f => f.name).sort());
}

export function WhatsNewAnnouncement() {
  const dispatch = useDispatch();
  const userId = useSelector(state => state.users.currentUser?.id);
  const enabledFeatures = useSelector(getEnabledNewFeatures);
  const eventId = useEventId();
  const [chatbotEnabledForEvent, setChatbotEnabledForEvent] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const prevUserIdRef = useRef(userId);
  const chatbotGloballyEnabled = enabledFeatures.some(f => f.name === 'chatbot');

  // AppLayout (where this mounts) wraps /login and never remounts on login, so this must
  // wait for a real user rather than firing once, unauthenticated, at initial page load.
  useEffect(() => {
    if (!userId) return;
    dispatch(getWhatsNewConfig());
  }, [dispatch, userId]);

  // Reset the seen-flag on user switch/login so a new session always sees the popup once.
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      sessionStorage.removeItem(SEEN_KEY);
    }
    prevUserIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    if (!chatbotGloballyEnabled || !eventId) {
      setChatbotEnabledForEvent(false);
      return;
    }

    setChatbotEnabledForEvent(false); // don't carry over the previous event's enabled state

    let cancelled = false;

    http.get(`${Config.apiUrl}/testcenterevent/${eventId}/chatbot/config`)
      .then((data) => {
        if (!cancelled) setChatbotEnabledForEvent(!!data.enabled);
      })
      .catch(() => { /* chatbot config unavailable — degrade silently */ });

    return () => { cancelled = true; };
  }, [chatbotGloballyEnabled, eventId]);

  const active = chatbotGloballyEnabled && chatbotEnabledForEvent;
  const featuresKey = active ? getSnapshot(enabledFeatures) : null;

  useEffect(() => {
    if (!featuresKey) return;
    if (sessionStorage.getItem(SEEN_KEY) !== featuresKey) {
      setShowModal(true);
    }
    // userId is included so a login/user-switch (which clears the seen-flag above,
    // in an effect that runs earlier in the same commit) re-evaluates immediately.
  }, [featuresKey, userId]);

  if (!active) return null;

  function dismiss() {
    sessionStorage.setItem(SEEN_KEY, featuresKey);
    setShowModal(false);
  }

  return (
    <>
      <div className="whats-new-banner-wrapper">
        <WhatsNewBanner onLearnMore={() => setShowModal(true)} />
      </div>
      <WhatsNewModal isOpen={showModal} features={enabledFeatures} onDismiss={dismiss} />
    </>
  );
}
